import type { ACTIONTYPE } from '@/layout/permissions/actions';

/**
 * A non-empty list of action codes, OR semantics — holding any one grants access.
 *
 * The tuple shape is deliberate: `roleActions: []` would pass a plain `ACTIONTYPE[]`
 * and silently gate on nothing, which reads as "no permission required" at every
 * call site that uses `.some()`. Making the empty case a compile error is the only
 * reliable way to keep an ungated item out of the menu.
 */
export type ActionSet = readonly [ACTIONTYPE, ...ACTIONTYPE[]];

/** Helper so a literal list infers as a tuple rather than an array. */
export const actionSet = <T extends ActionSet>(...actions: T): T => actions;

/**
 * Where the current permission set came from.
 *
 * - `jwt`     — read from the token's `action` claims. Synchronous, no network.
 * - `api`     — fetched from `POST /Action/getActionsForMultipleRoles` (legacy token).
 * - `unresolved` — no answer yet, or the fetch failed. NEVER deny on this.
 */
export type PermissionSource = 'jwt' | 'api' | 'unresolved';

export interface IPermissionSnapshot {
	actions: ReadonlySet<ACTIONTYPE>;
	roles: readonly string[];
	source: PermissionSource;
	/**
	 * True once a real answer exists — either the token carried `action` claims or
	 * the fallback fetch succeeded. While false, callers must fail OPEN.
	 *
	 * Note this is distinct from `actions.size === 0`. A resolved-but-empty set is a
	 * real answer ("this user holds nothing") and must fail CLOSED; an unresolved set
	 * usually means the session lapsed, and denying there sends an operator to their
	 * administrator for a problem a re-login fixes.
	 */
	isResolved: boolean;
}

export const EMPTY_SNAPSHOT: IPermissionSnapshot = {
	actions: new Set<ACTIONTYPE>(),
	roles: [],
	source: 'unresolved',
	isResolved: false,
};
